import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { isCancel } from 'axios'
import { listInvoices } from '@/api/invoices'
import { formatCurrency } from '@/lib/format'
import { INVOICE_STATUS_LABELS, findInvoiceForShipment, formatInvoiceLabel } from '@/lib/invoice'
import type { Invoice } from '@/api/types'

const LABEL = 'Faktura'
const LOADING_MESSAGE = 'Laddar...'
const NO_INVOICE_MESSAGE = 'Ingen faktura ännu'
const ERROR_MESSAGE = 'Kunde inte hämta fakturan'

interface ShipmentInvoiceSummaryProps {
  shipmentId: number
  customerId: number
  // Får leveransens faktura, eller null, varje gång den hämtats. Ska vara stabil, t.ex. en state-setter.
  onInvoiceLoaded?: (invoice: Invoice | null) => void
}

function formatInvoiceSummary(invoice: Invoice): string {
  return `${formatInvoiceLabel(invoice)} · ${INVOICE_STATUS_LABELS[invoice.status]} · ${formatCurrency(Number(invoice.amount))}`
}

export function ShipmentInvoiceSummary({ shipmentId, customerId, onInvoiceLoaded }: ShipmentInvoiceSummaryProps): React.ReactElement {
  const [invoice, setInvoice] = useState<Invoice | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [hasError, setHasError] = useState(false)

  const hasInvoice = invoice !== null
  const showNone = !isLoading && !hasError && !hasInvoice
  const summaryText = invoice === null ? '' : formatInvoiceSummary(invoice)
  const href = `/invoices/${invoice?.id ?? ''}`

  useEffect(() => {
    const controller = new AbortController()

    const loadInvoice = async (): Promise<void> => {
      setIsLoading(true)
      setHasError(false)

      try {
        const data = await listInvoices({ customer_id: customerId }, controller.signal)

        const found = findInvoiceForShipment(data, shipmentId) ?? null

        setInvoice(found)
        onInvoiceLoaded?.(found)
      } catch (error) {
        if (isCancel(error)) return

        setHasError(true)
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false)
        }
      }
    }

    void loadInvoice()

    return () => controller.abort()
  }, [customerId, shipmentId, onInvoiceLoaded])

  return (
    <div>
      <p className="text-sm text-muted-foreground">{LABEL}</p>
      {isLoading && <p className="text-muted-foreground">{LOADING_MESSAGE}</p>}
      {hasError && <p className="text-muted-foreground">{ERROR_MESSAGE}</p>}
      {showNone && <p>{NO_INVOICE_MESSAGE}</p>}
      {hasInvoice && (
        <Link
          to={href}
          className="underline"
        >
          {summaryText}
        </Link>
      )}
    </div>
  )
}
